import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Checkbox,
    Chip,
    FormControlLabel,
    MenuItem,
    Popover,
    Select,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';

export const getParamEntry = (mavId, name) => {
    if (!mavId || !window.paramList || !window.paramList[mavId]) return null;
    return window.paramList[mavId][name] || null;
};

export const getParamValue = (mavId, name) => {
    const e = getParamEntry(mavId, name);
    return e ? e.value : null;
};

export const getMeta = (entry) => {
    const md = entry && entry.metaData ? entry.metaData : null;
    const values = md && md.values && typeof md.values === 'object' ? md.values : {};
    const bitmask = md && md.bitmask && typeof md.bitmask === 'object' ? md.bitmask : {};
    const fields = md && md.fields && typeof md.fields === 'object' ? md.fields : {};
    const hasValues = Object.keys(values).length > 0;
    const hasBitmask = Object.keys(bitmask).length > 0;
    const documentation = md && md.documentation ? md.documentation : '';
    const humanName = md && md.humanName ? md.humanName : '';
    return { md, values, bitmask, fields, hasValues, hasBitmask, documentation, humanName };
};

// Compact bitmask editor: shows summary chip; clicking opens a popover with checkboxes.
export const BitmaskEditor = ({ value, bitmask, disabled, onCommit }) => {
    const [anchor, setAnchor] = useState(null);
    const numeric = Number.isFinite(Number(value)) ? (Number(value) >>> 0) : 0;
    const entries = Object.keys(bitmask).map((label) => ({ label, bit: parseInt(bitmask[label], 10) }));
    const enabled = entries.filter((b) => (numeric & (1 << b.bit)) !== 0);
    const summary = numeric === 0
        ? 'none'
        : (enabled.length <= 2
            ? enabled.map((b) => b.label).join(', ')
            : `${enabled.length} bits set`);
    return (
        <>
            <Tooltip title={disabled ? 'Enable edit to modify' : 'Click to edit bitmask'} placement="top" arrow>
                <span style={{ width: '100%' }}>
                    <Chip
                        size="small"
                        variant="outlined"
                        label={`0x${numeric.toString(16).toUpperCase()} \u2014 ${summary}`}
                        onClick={disabled ? undefined : (e) => setAnchor(e.currentTarget)}
                        sx={{ width: '100%', justifyContent: 'space-between', fontSize: 11, cursor: disabled ? 'not-allowed' : 'pointer' }}
                    />
                </span>
            </Tooltip>
            <Popover
                open={!!anchor}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Box sx={{ p: 1, maxHeight: 300, overflow: 'auto', minWidth: 240 }}>
                    {entries.map((b) => {
                        const bv = 1 << b.bit;
                        const checked = (numeric & bv) !== 0;
                        return (
                            <FormControlLabel
                                key={b.label}
                                sx={{ display: 'flex', mr: 0 }}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={checked}
                                        onChange={() => onCommit((numeric ^ bv) >>> 0)}
                                    />
                                }
                                label={<Typography variant="caption">{b.label} <span style={{ opacity: 0.5 }}>(bit {b.bit})</span></Typography>}
                            />
                        );
                    })}
                </Box>
            </Popover>
        </>
    );
};

export const ParamEditor = ({ entry, disabled, onCommit }) => {
    const meta = getMeta(entry);
    const [text, setText] = useState(String(entry.value ?? ''));
    const lastValueRef = useRef(entry.value);
    useEffect(() => {
        if (entry.value !== lastValueRef.current) {
            lastValueRef.current = entry.value;
            setText(String(entry.value ?? ''));
        }
    }, [entry.value]);

    const commitNumeric = () => {
        const n = Number(text);
        if (!Number.isFinite(n)) {
            setText(String(entry.value ?? ''));
            return;
        }
        if (n === Number(entry.value)) return;
        onCommit(n, entry.type);
    };

    const commitValue = (v) => {
        if (Number(v) === Number(entry.value)) return;
        onCommit(Number(v), entry.type);
    };

    if (meta.hasValues) {
        const optionEntries = Object.keys(meta.values).map((label) => ({
            label,
            value: Number(meta.values[label]),
        }));
        const currentNum = Number(entry.value);
        const known = optionEntries.some((o) => o.value === currentNum);
        return (
            <Select
                size="small"
                value={currentNum}
                disabled={disabled}
                onChange={(e) => commitValue(e.target.value)}
                renderValue={(v) => {
                    const found = optionEntries.find((o) => o.value === Number(v));
                    return found ? `${found.label} (${found.value})` : `Unknown (${v})`;
                }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 360, fontSize: 12 } }, disableScrollLock: true }}
                sx={{ width: '100%', fontSize: 12, '& .MuiSelect-select': { py: 0.5 } }}
            >
                {!known && (
                    <MenuItem value={currentNum} dense disabled>
                        Unknown ({currentNum})
                    </MenuItem>
                )}
                {optionEntries.map((o) => (
                    <MenuItem key={o.value} value={o.value} dense>
                        {o.label} ({o.value})
                    </MenuItem>
                ))}
            </Select>
        );
    }
    if (meta.hasBitmask) {
        return (
            <BitmaskEditor
                value={entry.value}
                bitmask={meta.bitmask}
                disabled={disabled}
                onCommit={(v) => commitValue(v)}
            />
        );
    }
    const step = meta.fields.Increment ? meta.fields.Increment : undefined;
    const unitText = meta.fields.Units || meta.fields.UnitText || '';
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
            <TextField
                size="small"
                value={text}
                disabled={disabled}
                onChange={(e) => setText(e.target.value)}
                onBlur={commitNumeric}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.target.blur(); }
                    else if (e.key === 'Escape') { setText(String(entry.value ?? '')); e.target.blur(); }
                }}
                inputProps={{
                    inputMode: 'numeric',
                    step,
                    style: { textAlign: 'right', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontVariantNumeric: 'tabular-nums', padding: '4px 6px' },
                }}
                sx={{ flex: 1, '& .MuiInputBase-root': { fontSize: 12 } }}
            />
            {unitText && (
                <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 18, textAlign: 'left' }}>
                    {unitText}
                </Typography>
            )}
        </Box>
    );
};

const ParamRow = ({
    mavId, name, desc, disabled, onCommit, paramListTimestamp,
    gridTemplateColumns = '200px 1fr 220px',
    hideDescription = false,
}) => {
    const entry = getParamEntry(mavId, name);
    const meta = getMeta(entry);
    // bump child editor when paramListTimestamp changes externally
    const [, force] = useState(0);
    useEffect(() => { force((n) => n + 1); }, [paramListTimestamp]);
    if (!entry) return null;

    const description = desc || meta.documentation || meta.humanName || '';

    const tooltipTitle = (
        <Box sx={{ maxWidth: 320 }}>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>{name}{meta.humanName ? ` \u2014 ${meta.humanName}` : ''}</Typography>
            {meta.documentation && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>{meta.documentation}</Typography>
            )}
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7 }}>type {entry.type}{meta.fields.Range ? ` \u2014 range ${meta.fields.Range.split(' ').join(' .. ')}` : ''}</Typography>
        </Box>
    );

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns,
                alignItems: 'center',
                columnGap: 1.5,
                py: 0.5,
            }}
        >
            <Tooltip title={tooltipTitle} placement="top" arrow>
                <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 12 }} noWrap>
                    {name}
                </Typography>
            </Tooltip>
            {!hideDescription && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap title={description}>
                    {description}
                </Typography>
            )}
            <ParamEditor
                entry={entry}
                disabled={disabled}
                onCommit={(value, type) => onCommit(name, value, type)}
            />
        </Box>
    );
};

export default ParamRow;
